import { hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { LinkTo } from '@ember/routing';
import { type Registry as Services, service } from '@ember/service';
import { PrismIcon } from '@mylighthouse/prism-ember';
import EmberTooltip from '@mylighthouse/prism-ember/components/ember-tooltip';
import { and } from 'ember-truth-helpers';
import HotelRatesTablesCellsTdBase from 'lumen/components/hotel/rates/tables/cells/td-base';
import HotelRatesTooltipsShortTermRentalTooltip from 'lumen/components/hotel/rates/tooltips/short-term-rental-tooltip';
import changeColorClass from 'lumen/helpers/change-color-class';
import { COMPARETYPES } from 'lumen/utils/constants/general-constants';
import { DETAIL_TABS } from 'lumen/utils/constants/rate-constants';
import { isComparingRates } from 'lumen/utils/is-comparing-rates';

export default class HotelRatesTablesCellsTdShortTermRentalOtb extends HotelRatesTablesCellsTdBase {
  @service declare router: Services['router'];

  DETAIL_TABS = DETAIL_TABS;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get differenceForCompare() {
    if (!this.compareValue) {
      return null;
    }
    return this.period.rentalOccupancyForCurrentDay?.differenceForExtractDate(
      this.compareValue,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedDifferenceForCompare() {
    if (!this.compareValue) {
      return null;
    }
    return this.period.rentalOccupancyForCurrentDay?.formattedDifferenceForExtractDate(
      this.compareValue,
    );
  }

  get layoutConditionalClassNames(): string {
    const { layout, textAlign } = this.args.column;
    const alignment = textAlign === 'left' ? 'items-start' : 'items-end';
    const justify = textAlign === 'left' ? 'justify-start' : 'justify-end';
    const isRowLayout = layout === 'side-by-side';

    return isRowLayout
      ? `td-short-term-rental--row ${justify}`
      : `td-short-term-rental--column ${alignment}`;
  }

  get showAlerts(): boolean {
    return (
      !isComparingRates(this.compareType, this.compareValue) &&
      this.period.hasOTBShortTermRentalAlert
    );
  }

  get showOTBChanges(): boolean {
    const isDateCompare = this.compareType === COMPARETYPES.DATE;
    return isDateCompare && !this.isPast;
  }

  @action
  redirectToShortTermRental(): void {
    const currentDay = this.period?.day?.date?.format('YYYY-MM-DD');
    this.router.transitionTo('hotel.short-term-rental', {
      queryParams: { fromDate: currentDay, toDate: currentDay },
    });
  }

  <template>
    <div class="tabular-nums">
      {{#if this.showAlerts}}
        <div class="alert-rate-change mr-300 flex items-center">
          <button
            class="button--unstyled is-link mr-100"
            type="button"
            {{on "click" this.redirectToShortTermRental}}
          >
            <PrismIcon @name="short-term-rental" @size="extra-small" />
          </button>
          <span class="alert-indicator"></span>
          <EmberTooltip
            @delay={{25}}
            @side="right"
            @tooltipClass="base-tooltip tooltip-short-term-rental"
          >
            <HotelRatesTooltipsShortTermRentalTooltip
              @hasOTBShortTermRentalAlert={{true}}
              @hasRateShortTermRentalAlert={{false}}
              @hasShortTermRentalAlert={{true}}
              @period={{this.period}}
              @showOnlyAlert={{true}}
            />
          </EmberTooltip>
        </div>
      {{/if}}

      <div class="mr-500 {{this.layoutConditionalClassNames}}">
        <div
          class="text-200--regular flex items-center justify-center truncate
            {{if this.isPast 'td-past'}}
            {{if this.isToday 'td-today'}}"
        >
          <LinkTo
            @query={{hash
              date=this.period.day.formattedDate
              type=this.DETAIL_TABS.BOOKING_CURVE
            }}
            @route="hotel.rates.detail"
            class="link truncate"
          >
            {{this.period.rentalOccupancyForCurrentDay.formattedValue}}
          </LinkTo>
        </div>
        {{#if (and this.showOTBChanges this.differenceForCompare)}}
          <div
            class="td-right-value
              {{changeColorClass this.differenceForCompare}}"
          >
            {{this.formattedDifferenceForCompare}}
          </div>
        {{else if this.compareType}}
          &nbsp;
        {{/if}}
      </div>
    </div>
  </template>
}
